import { Fragment } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiPost } from '../api/client.js';
import { platformIcon, platformName } from '../lib/platforms.js';
import { TikTokOptionsButton, isReelPost } from './TikTokOptions.jsx';

/**
 * PostPublications — compact per-social-network publication status for a post
 * row, with inline retry on a failed platform. Reads post.publications
 * (added to GET /posts): [{ platform, state }]. Sits inside the row's State
 * cell; clicks are stopped so they don't trigger the row's navigation Link.
 *
 * Each network shows as its platform ICON (not a text initial); publish state
 * is carried by the chip's colour/background. The TikTok chip is grouped with
 * its options ⚙ gear so the two never wrap apart.
 *
 * The retry mirrors PostDetail's PublicationCard — POST
 * /posts/:id/publications/:platform/retry — so one failed platform can be
 * re-published without opening the post.
 */

// Human tooltip per chip. Skipped and failed chips carry the WHY from the
// publication row (error_message) so an icon chip is never a mystery.
function chipTitle(pub) {
  const name = platformName(pub.platform);
  if (pub.state === 'skipped') {
    return `${name}: skipped — ${pub.error_message || 'not applicable for this platform'}`;
  }
  if (pub.state === 'failed') {
    return `${name}: failed${pub.error_message ? ` — ${pub.error_message}` : ''} — click ↻ to retry`;
  }
  return `${name}: ${pub.state}`;
}

export function PostPublications({ post }) {
  const qc = useQueryClient();
  const pubs = post.publications || [];
  const hasTikTokChip = pubs.some((p) => p.platform === 'tiktok');

  const retry = useMutation({
    mutationFn: (platform) => apiPost(`/posts/${post.id}/publications/${platform}/retry`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['posts'] }),
  });

  // Show nothing only when there are no chips AND TikTok isn't a target — the
  // ⚙ gear rides with the TikTok chip (or stands in for it before publish).
  if (!pubs.length && !post.tiktok_account_id) return null;

  // The TikTok options ⚙ lives INSIDE the TikTok chip (🎵 ⚙) so it's
  // unmistakably TikTok's — never a loose gear that could belong to any row.
  const renderChip = (pub) => (
    <span className={`pub-chip pub-${pub.state}${pub.platform === 'tiktok' ? ' pub-chip-tiktok' : ''}`} title={chipTitle(pub)}>
      <span className="pub-icon" aria-hidden="true">{platformIcon(pub.platform)}</span>
      <span className="sr-only">{platformName(pub.platform)}</span>
      {pub.state === 'failed' && (
        <button
          type="button"
          className="pub-retry"
          disabled={retry.isPending}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); retry.mutate(pub.platform); }}
          aria-label={`Retry ${platformName(pub.platform)}`}
          title={`Retry ${platformName(pub.platform)}`}
        >
          {retry.isPending ? '…' : '↻'}
        </button>
      )}
      {pub.platform === 'tiktok' && <TikTokOptionsButton post={post} className="tt-opt-inchip" />}
    </span>
  );

  return (
    <span
      className="post-pubs"
      // Keep clicks inside the chips from navigating the row Link.
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
    >
      {pubs.map((pub) => <Fragment key={pub.platform}>{renderChip(pub)}</Fragment>)}
      {/* TikTok targeted but not published yet — still show a TikTok chip so
          its ⚙ is clearly TikTok's (not a floating gear). Skip for reels
          (TikTok video isn't supported yet) using the SAME isReelPost the
          gear itself applies — the inline pillar_key-only copy missed
          payload.format==='reel', rendering a forever-pending chip with no
          gear for posts publishWorker will actually skip (review 2026-08-24). */}
      {!hasTikTokChip && post.tiktok_account_id && !isReelPost(post)
        && renderChip({ platform: 'tiktok', state: 'pending' })}
    </span>
  );
}
